import React, { useState, useEffect, useContext } from "react";
import { getRequest, postRequest } from "../../helpers/ApiHelpers";
import { css } from "emotion";
import { Button, Header, Grid, Form, TextArea, Input, Dropdown, Checkbox, Message, Modal } from "semantic-ui-react";
import shortid from "shortid";
import { LatestContactUpdatedContext } from "../LatestContactUpdated";
import FileUpload from "../FileUpload";
import { readFileAsBase64 } from "../../helpers/ReadFileAsBase64";
import { logEvent } from "../../helpers/analyticsLogger";
import { eAndODepartments } from "./eAndODepartments";

export function EditAddContact({ match, history }) {
  const defaultContact = {
    id: 0,
    address1: null,
    address2: null,
    cell: null,
    city: null,
    firmName: null,
    departmentId: +match.params.department,
    dept: null,
    districtId: null,
    email: null,
    faxDirect: null,
    faxMain: null,
    firstName: null,
    isCommercial: null,
    isPrimary: null,
    isResidential: null,
    lastName: null,
    note: null,
    phoneDirect: null,
    phoneMain: null,
    position: null,
    state: null,
    customTag: null,
    underwriterId: null,
    website: null,
    zip: null,
    contactStates: [{ contactId: 0, stateId: +match.params.state }],
    isGlobal: false,
    isLastOption: false
  };

  const [state, setState] = useState({
    contact: defaultContact,
    districts: [{}],
    departments: [{}],
    underwriters: [{}],
    states: [{}],
    companies: [{}],
    fullCompanies: [{}],
    submitAttempted: false,
    errorList: [],
    submitting: false,
    submitted: false,
    editMode: match.path === "/contacts/edit-contact/:id",
    isCompanyContact: false,
    contactTags: [],
    allStates: false,
    allLicensedStates: false,
    allUnlicensedStates: false,
    customStates: true,
    allStatesList: [],
    allLicensedStatesList: [],
    allUnlicensedStatesList: [],
    statesWithPrimaryContact: [],
    confirmSaveAsPrimary: false,
    saveAsPrimaryConfirmed: false,
    files: [],
    filesToDelete: []
  });

  const { updateLatestContactUpdated } = useContext(LatestContactUpdatedContext);

  async function getContactTags() {
    const result = await getRequest(`/contact/getContactTags`);
    if (result.success) {
      setState(state => ({
        ...state,
        contactTags: [
          {
            key: null,
            value: null,
            text: "None"
          },
          ...result.data.map(c => ({
            key: c.id,
            text: c.name,
            value: c.id
          }))
        ]
      }));
    }
  }

  async function getContactById(contactId) {
    const result = await getRequest("/contact/GetContactById", {
      contactId
    });
    if (result.success) {
      setState(state => ({
        ...state,
        contact: {
          ...result.data,
          eAndODate: result.data.eAndODate ? new Date(result.data.eAndODate).toISOString().slice(0, 10) : null
        },
        isCompanyContact: result.data.companyId ? true : false
      }));
    }
  }

  async function getAllStatesList() {
    const result = await getRequest("/general/GetAllStates");
    if (result.success) {
      setState(state => ({
        ...state,
        allStatesList: result.data
      }));
    }
  }

  async function getAllLicensedStatesList() {
    const result = await getRequest("/general/GetAllLicensedStates");
    if (result.success) {
      setState(state => ({
        ...state,
        allLicensedStatesList: result.data
      }));
    }
  }

  async function getAllUnlicensedStatesList() {
    const result = await getRequest("/general/GetAllUnlicensedStates");
    if (result.success) {
      setState(state => ({
        ...state,
        allUnlicensedStatesList: result.data
      }));
    }
  }

  async function getDistricts() {
    const result = await getRequest("/general/getDistricts");
    if (result.success) {
      setState(state => ({
        ...state,
        districts: [
          {
            key: null,
            value: null,
            text: "N/A"
          },
          ...result.data.map(d => ({
            key: d.id,
            value: d.id,
            text: d.name
          }))
        ]
      }));
    }
  }

  async function getDepartments() {
    const result = await getRequest("/general/getDepartments");
    if (result.success) {
      setState(state => ({
        ...state,
        departments: result.data.map(d => ({
          key: d.id,
          value: d.id,
          text: d.name
        }))
      }));
    }
  }

  async function getUnderwriters() {
    const result = await getRequest("/underwriter/getUnderwriters");
    if (result.success) {
      setState(state => ({
        ...state,
        underwriters: result.data
          .filter(u => u.name !== "All Underwriters")
          .map(u => ({
            key: u.id,
            value: u.id,
            text: u.name
          }))
      }));
    }
  }

  async function getCompanies() {
    const result = await getRequest("/company/getCompanies");
    if (result.success) {
      setState(state => ({
        ...state,
        fullCompanies: result.data,
        companies: [{ key: 0, value: null, text: "None" }].concat(
          result.data.map(u => ({
            key: u.id,
            value: u.id,
            text: u.name
          }))
        )
      }));
    }
  }

  async function getStates() {
    const result = await getRequest("/general/getStates");
    if (result.success) {
      setState(state => ({
        ...state,
        states: result.data.map(s => ({
          key: s.id,
          value: s.id,
          text: s.name
        }))
      }));
    }
  }

  async function saveChanges() {
    if (!state.submitting) {
      setState(state => ({
        ...state,
        submitAttempted: true
      }));
      if (!state.errorList.length > 0) {
        setState(state => ({ ...state, submitting: true }));
        const result = await postRequest(`/contact/${state.editMode ? "editContact" : "addContact"}`, {
          ...state.contact,
          files: await Promise.all(
            state.files.map(async f => ({
              base64File: await readFileAsBase64(f),
              fileName: f.name
            }))
          ),
          filesToDelete: state.filesToDelete,
          newTag: state.newTag
        });
        if (result.success) {
          logEvent(null, state.editMode ? "Edited" : "Added", "Contact");
          const updatedSelect = result.data;
          updatedSelect === 1 && updateLatestContactUpdated(state.contact.firstName + " " + state.contact.lastName);
          updatedSelect === 3 && updateLatestContactUpdated("error");
          setState(
            state => ({
              ...state,
              submitting: false,
              submitted: true
            }),
            history.goBack()
          );
        }
      }
    }
  }

  async function checkIfStateHasPrimaryContact() {
    const result = await getRequest("/contact/checkIfStateHasPrimaryContact", {
      contactId: state.contact.id,
      states: state.contact.contactStates.map(cs => cs.stateId),
      departmentId: state.contact.departmentId,
      underwriterId: state.contact.underwriterId
    });
    if (result.success) {
      if (result.data.length < 1) {
        saveChanges();
      } else {
        setState(state => ({
          ...state,
          statesWithPrimaryContact: result.data,
          confirmSaveAsPrimary: result.data
        }));
      }
    }
  }

  useEffect(() => {
    getContactTags();
    getDistricts();
    getDepartments();
    getUnderwriters();
    getStates();
    getCompanies();
    getAllStatesList();
    getAllLicensedStatesList();
    getAllUnlicensedStatesList();
    state.editMode && getContactById(match.params.id);
  }, []);

  useEffect(() => {
    if (state.editMode) {
      let checkIfContainsAll = (arr, target) => target.every(v => arr.includes(v));
      setState(state => ({
        ...state,
        allStates: checkIfContainsAll(
          state.contact.contactStates.length > 0 ? state.contact.contactStates.map(cs => cs.stateId) : [],
          state.allStatesList
        ),
        allLicensedStates:
          checkIfContainsAll(
            state.contact.contactStates.length > 0 ? state.contact.contactStates.map(cs => cs.stateId) : [],
            state.allLicensedStatesList
          ) && state.contact.contactStates.length === state.allLicensedStatesList.length,
        allUnlicensedStates:
          checkIfContainsAll(
            state.contact.contactStates.length > 0 ? state.contact.contactStates.map(cs => cs.stateId) : [],
            state.allUnlicensedStatesList
          ) && state.contact.contactStates.length === state.allUnlicensedStatesList.length
      }));
    } else {
      return;
    }
  }, [state.contact.id, state.allStatesList, state.allLicensedStatesList, state.allUnlicensedStatesList]);

  useEffect(() => {
    setState(state => ({
      ...state,
      customStates: !state.allLicensedStates && !state.allUnlicensedStates && !state.allStates
    }));
  }, [state.allStates, state.allUnlicensedStates, state.allLicensedStates]);

  function getErrorList() {
    let list = [];
    if (state.contact.firstName === null || state.contact.firstName === "") {
      list.push("You must provide a first name.");
    }
    if (state.contact.lastName === null || state.contact.lastName === "") {
      list.push("You must provide a last name.");
    }
    if (state.contact.departmentId === null) {
      list.push("You must select a department.");
    }
    if (
      state.contact.underwriterId === null &&
      (state.contact.departmentId === 1 || state.contact.departmentId === 7 || state.contact.departmentId === 11)
    ) {
      list.push("You must select an underwriter type.");
    }
    if (!state.contact.contactStates.length > 0) {
      list.push("You must select at least one servicing state.");
    }
    return list;
  }

  useEffect(() => {
    setState(state => ({ ...state, errorList: getErrorList() }));
  }, [
    state.contact.firstName,
    state.contact.lastName,
    state.contact.email,
    state.contact.email,
    state.contact.departmentId,
    state.contact.underwriterId,
    state.contact.contactStates,
    state.submitAttempted
  ]);

  function handleInputChange(value, prop) {
    setState(state => ({
      ...state,
      contact: { ...state.contact, [prop]: value }
    }));
  }

  function buildContactStates(value) {
    return value.map(v => ({
      contactId: 0,
      stateId: v
    }));
  }

  return (
    <React.Fragment>
      <div className={css({ width: 605, paddingBottom: 34 })}>
        <Header style={{ fontSize: 24, marginBottom: 41 }} as="h3">
          {`${state.editMode ? "Edit" : "New"} Contact`}
        </Header>
        {!state.editMode && (
          <div
            className={css({
              padding: 8,
              backgroundColor: "#f7f7f9",
              borderRadius: 5,
              display: "inline-block",
              marginBottom: 32
            })}
          >
            <Button
              onClick={() =>
                history.replace(
                  `/contacts/new-contact/states/${+match.params.state}/departments/${+match.params.department}`
                )
              }
              size="large"
              className={css({
                backgroundColor: "#000e48 !important",
                color: "#fff !important"
              })}
              data-automation="newContact"
            >
              Individual
            </Button>
            <Button
              onClick={() =>
                history.replace(
                  `/contacts/new-company/states/${+match.params.state}/departments/${+match.params.department}`
                )
              }
              size="large"
              className={css({
                backgroundColor: "transparent !important",
                color: "#444444 !important",
                "&:hover": { opacity: 0.75 }
              })}
              data-automation="newCompany"
            >
              Company
            </Button>
          </div>
        )}
        <Form>
          <Grid>
            <Grid.Row columns={2}>
              <Grid.Column>
                <Form.Field required>
                  <label>First Name</label>
                  <Input
                    size="large"
                    value={state.contact.firstName}
                    onChange={(_, { value }) => handleInputChange(value, "firstName")}
                    error={
                      state.submitAttempted && (state.contact.firstName === "" || state.contact.firstName === null)
                    }
                    autoComplete="new-password"
                    data-automation="firstName"
                  />
                </Form.Field>
              </Grid.Column>
              <Grid.Column>
                <Form.Field required>
                  <label>Last Name</label>
                  <Input
                    size="large"
                    value={state.contact.lastName}
                    onChange={(_, { value }) => handleInputChange(value, "lastName")}
                    error={state.submitAttempted && (state.contact.lastName === "" || state.contact.lastName === null)}
                    autoComplete="new-password"
                    data-automation="lastName"
                  />
                </Form.Field>
              </Grid.Column>
            </Grid.Row>
            <Grid.Row columns={2}>
              <Grid.Column>
                <Form.Field>
                  <label>Position</label>
                  <Input
                    size="large"
                    value={state.contact.position}
                    onChange={(_, { value }) => handleInputChange(value, "position")}
                    autoComplete="new-password"
                    data-automation="position"
                  />
                </Form.Field>
              </Grid.Column>
            </Grid.Row>
            <Grid.Row columns={1}>
              <Grid.Column>
                <Form.Field>
                  <label>Address</label>
                  <Input
                    size="large"
                    value={state.contact.address1}
                    onChange={(_, { value }) => handleInputChange(value, "address1")}
                    autoComplete="new-password"
                    data-automation="address1"
                  />
                </Form.Field>
                <Form.Field>
                  <Input
                    size="large"
                    placeholder="Attention, suite, unit, building, floor, etc."
                    value={state.contact.address2}
                    onChange={(_, { value }) => handleInputChange(value, "address2")}
                    autoComplete="new-password"
                    data-automation="address2"
                  />
                </Form.Field>
              </Grid.Column>
            </Grid.Row>
            <Grid.Row columns={1}>
              <Grid.Column>
                <Form.Field>
                  <label>City</label>
                  <Input
                    size="large"
                    value={state.contact.city}
                    onChange={(_, { value }) => handleInputChange(value, "city")}
                    autoComplete="new-password"
                    data-automation="city"
                  />
                </Form.Field>
              </Grid.Column>
            </Grid.Row>
            <Grid.Row columns={2}>
              <Grid.Column>
                <Form.Field>
                  <label>State</label>
                  <Input
                    size="large"
                    value={state.contact.state}
                    onChange={(_, { value }) => handleInputChange(value, "state")}
                    autoComplete="new-password"
                    data-automation="state"
                  />
                </Form.Field>
              </Grid.Column>
              <Grid.Column>
                <Form.Field>
                  <label>Zip</label>
                  <Input
                    size="large"
                    value={state.contact.zip}
                    onChange={(_, { value }) => handleInputChange(value, "zip")}
                    autoComplete="new-password"
                    data-automation="zip"
                  />
                </Form.Field>
              </Grid.Column>
            </Grid.Row>
            <Grid.Row columns={2}>
              <Grid.Column>
                <Form.Field>
                  <label>Phone</label>
                  <Input
                    size="large"
                    value={state.contact.phoneDirect}
                    onChange={(_, { value }) => handleInputChange(value, "phoneDirect")}
                    autoComplete="new-password"
                    data-automation="phone"
                  />
                </Form.Field>
              </Grid.Column>
              <Grid.Column>
                <Form.Field>
                  <label>Cell</label>
                  <Input
                    size="large"
                    value={state.contact.cell}
                    onChange={(_, { value }) => handleInputChange(value, "cell")}
                    autoComplete="new-password"
                    data-automation="cell"
                  />
                </Form.Field>
              </Grid.Column>
            </Grid.Row>
            <Grid.Row columns={2}>
              <Grid.Column>
                <Form.Field>
                  <label>Fax</label>
                  <Input
                    size="large"
                    value={state.contact.faxDirect}
                    onChange={(_, { value }) => handleInputChange(value, "faxDirect")}
                    autoComplete="new-password"
                    data-automation="fax"
                  />
                </Form.Field>
              </Grid.Column>
            </Grid.Row>
            <Grid.Row columns={2}>
              <Grid.Column>
                <Form.Field>
                  <label>Email</label>
                  <Input
                    size="large"
                    value={state.contact.email}
                    onChange={(_, { value }) => handleInputChange(value, "email")}
                    autoComplete="new-password"
                    data-automation="email"
                  />
                </Form.Field>
              </Grid.Column>
            </Grid.Row>
            <Grid.Row columns={1}>
              <Grid.Column>
                <Form.Field>
                  <Checkbox
                    label="Company contact"
                    checked={state.isCompanyContact}
                    size="large"
                    onChange={() =>
                      setState(state => ({
                        ...state,
                        contact: {
                          ...state.contact,
                          firmName: !state.isCompanyContact ? null : state.contact.firmName,
                          companyId: !state.isCompanyContact ? state.companyId : null
                        },
                        isCompanyContact: !state.isCompanyContact
                      }))
                    }
                    data-automation="isCompanyContact"
                  />
                </Form.Field>
              </Grid.Column>
            </Grid.Row>
            <Grid.Row columns={1}>
              {state.isCompanyContact ? (
                <Grid.Column>
                  <Form.Field>
                    <label>Company</label>
                    <Dropdown
                      size="large"
                      fluid
                      selection
                      placeholder="Select Company"
                      style={{ fontSize: 16 }}
                      options={state.companies}
                      value={state.contact.companyId}
                      onChange={(_, { value }) => {
                        const company = state.fullCompanies.find(c => c.id === value);
                        setState(state => ({
                          ...state,
                          contact: {
                            ...state.contact,
                            companyId: value,
                            contactStates: company.companyStates,
                            departmentId: company.departmentId,
                            underwriterId: company.underwriterId
                          }
                        }));
                      }}
                      data-automation="company"
                    />
                  </Form.Field>
                </Grid.Column>
              ) : (
                <Grid.Column>
                  <Form.Field>
                    <label>Firm Name</label>
                    <Input
                      size="large"
                      value={state.contact.firmName}
                      onChange={(_, { value }) => handleInputChange(value, "firmName")}
                      autoComplete="new-password"
                      data-automation="firmName"
                    />
                  </Form.Field>
                </Grid.Column>
              )}
            </Grid.Row>
            <Grid.Row columns={1}>
              <Grid.Column>
                <Form.Field>
                  <Checkbox
                    label="All states"
                    checked={state.allStates}
                    size="large"
                    onClick={() =>
                      setState(state => ({
                        ...state,
                        allStates: true,
                        allLicensedStates: false,
                        allUnlicensedStates: false,
                        customStates: false,
                        contact: {
                          ...state.contact,
                          contactStates: buildContactStates(state.allStatesList),
                          departmentId: state.contact.departmentId === 19 ? null : state.contact.departmentId
                        }
                      }))
                    }
                    data-automation="allStates"
                  />
                </Form.Field>
                <Form.Field>
                  <Checkbox
                    label="All licensed states"
                    checked={state.allLicensedStates}
                    size="large"
                    onClick={() =>
                      setState(state => ({
                        ...state,
                        allStates: false,
                        allLicensedStates: true,
                        allUnlicensedStates: false,
                        customStates: false,
                        contact: {
                          ...state.contact,
                          contactStates: buildContactStates(state.allLicensedStatesList),
                          departmentId: state.contact.departmentId === 19 ? null : state.contact.departmentId
                        }
                      }))
                    }
                    data-automation="allLicensedStates"
                  />
                </Form.Field>
                <Form.Field>
                  <Checkbox
                    label="All unlicensed states"
                    checked={state.allUnlicensedStates}
                    size="large"
                    onClick={() =>
                      setState(state => ({
                        ...state,
                        allStates: false,
                        allLicensedStates: false,
                        allUnlicensedStates: true,
                        customStates: false,
                        contact: {
                          ...state.contact,
                          contactStates: buildContactStates(state.allUnlicensedStatesList),
                          departmentId: state.contact.departmentId === 19 ? null : state.contact.departmentId
                        }
                      }))
                    }
                    data-automation="allUnlicensedStates"
                  />
                </Form.Field>
                <Form.Field>
                  <Checkbox
                    label="Custom (choose your own state selection)"
                    checked={state.customStates}
                    size="large"
                    onClick={() =>
                      setState(state => ({
                        ...state,
                        allStates: false,
                        allLicensedStates: false,
                        allUnlicensedStates: false,
                        customStates: true
                      }))
                    }
                    data-automation="customStates"
                  />
                </Form.Field>
                {state.customStates && (
                  <Form.Field required>
                    <label>Servicing States</label>
                    <Dropdown
                      size="large"
                      fluid
                      multiple
                      selection
                      placeholder="Select States"
                      style={{ fontSize: 16 }}
                      options={state.states}
                      value={state.contact.contactStates ? state.contact.contactStates.map(cs => cs.stateId) : null}
                      onChange={(_, { value }) => {
                        setState(state => ({
                          ...state,
                          contact: {
                            ...state.contact,
                            contactStates:
                              value[value.length - 1] === 52
                                ? buildContactStates([52])
                                : buildContactStates(value.filter(v => v !== 52)),
                            departmentId:
                              value[value.length - 1] === 52
                                ? 19
                                : state.contact.departmentId === 19
                                ? null
                                : state.contact.departmentId
                          }
                        }));
                      }}
                      error={state.submitAttempted && !state.contact.contactStates.length > 0}
                      data-automation="servicingStates"
                    />
                  </Form.Field>
                )}
              </Grid.Column>
            </Grid.Row>
            {!state.contact.contactStates.some(cs => cs.stateId === 52) && (
              <Grid.Row columns={1}>
                <Grid.Column>
                  <Form.Field required>
                    <label>Department</label>
                    <Dropdown
                      size="large"
                      fluid
                      selection
                      placeholder="Select Department"
                      style={{ fontSize: 16 }}
                      options={state.departments}
                      value={state.contact.departmentId}
                      onChange={(_, { value }) =>
                        setState(state => ({
                          ...state,
                          contact: { ...state.contact, departmentId: value }
                        }))
                      }
                      error={state.submitAttempted && state.contact.departmentId === null}
                      data-automation="departments"
                    />
                  </Form.Field>
                </Grid.Column>
              </Grid.Row>
            )}
            {state.contact.departmentId === 1 ||
            state.contact.departmentId === 7 ||
            state.contact.departmentId === 11 ? (
              <Grid.Row columns={1}>
                <Grid.Column>
                  <Form.Field required>
                    <label>Underwriter</label>
                    <Dropdown
                      size="large"
                      fluid
                      selection
                      placeholder="Select Underwriter"
                      style={{ fontSize: 16 }}
                      options={state.underwriters}
                      value={state.contact.underwriterId}
                      onChange={(_, { value }) =>
                        setState(state => ({
                          ...state,
                          contact: { ...state.contact, underwriterId: value }
                        }))
                      }
                      error={state.submitAttempted && state.contact.underwriterId === null}
                      data-automation="underwriters"
                    />
                  </Form.Field>
                </Grid.Column>
              </Grid.Row>
            ) : null}
            {eAndODepartments.some(d => state.contact.departmentId === d) && (
              <React.Fragment>
                <Grid.Row columns={1}>
                  <Grid.Column>
                    <Form.Field>
                      <label>E & O</label>
                      <Checkbox
                        label="Y"
                        checked={state.contact.eAndO === "Y"}
                        size="large"
                        onClick={() => handleInputChange(state.contact.eAndO === "Y" ? null : "Y", "eAndO")}
                      />
                    </Form.Field>
                  </Grid.Column>
                  <Grid.Column>
                    <Form.Field>
                      <Checkbox
                        label="N"
                        checked={state.contact.eAndO === "N"}
                        size="large"
                        onClick={() => handleInputChange(state.contact.eAndO === "N" ? null : "N", "eAndO")}
                      />
                    </Form.Field>
                  </Grid.Column>
                  {(state.contact.departmentId === 6 || state.contact.departmentId === 20) && (
                    <Grid.Column>
                      <Form.Field>
                        <Checkbox
                          label="N/A"
                          checked={state.contact.eAndO === "N/A"}
                          size="large"
                          onClick={() => handleInputChange(state.contact.eAndO === "N/A" ? null : "N/A", "eAndO")}
                        />
                      </Form.Field>
                    </Grid.Column>
                  )}
                </Grid.Row>
                <Grid.Row>
                  <Grid.Column>
                    <Form.Field>
                      <label>E & O Date</label>
                      <Input
                        size="large"
                        type="date"
                        value={state.contact.eAndODate}
                        onChange={(_, { value }) => {
                          console.log(value);
                          handleInputChange(value, "eAndODate");
                        }}
                        autoComplete="new-password"
                      />
                    </Form.Field>
                  </Grid.Column>
                </Grid.Row>
                {(state.contact.departmentId === 6 || state.contact.departmentId === 20) && (
                  <React.Fragment>
                    <Grid.Row columns={1}>
                      <Grid.Column>
                        <Form.Field>
                          <label>Liability Disclaimer on Search</label>
                          <Checkbox
                            label="Y"
                            checked={state.contact.liabilityDisclaimerOnSearch === "Y"}
                            size="large"
                            onClick={() =>
                              handleInputChange(
                                state.contact.liabilityDisclaimerOnSearch === "Y" ? null : "Y",
                                "liabilityDisclaimerOnSearch"
                              )
                            }
                          />
                        </Form.Field>
                      </Grid.Column>
                      <Grid.Column>
                        <Form.Field>
                          <Checkbox
                            label="N"
                            checked={state.contact.liabilityDisclaimerOnSearch === "N"}
                            size="large"
                            onClick={() =>
                              handleInputChange(
                                state.contact.liabilityDisclaimerOnSearch === "N" ? null : "N",
                                "liabilityDisclaimerOnSearch"
                              )
                            }
                          />
                        </Form.Field>
                      </Grid.Column>
                    </Grid.Row>
                    <Grid.Row columns={1}>
                      <Grid.Column>
                        <Form.Field>
                          <label>Signed Agreement</label>
                          <Checkbox
                            label="Y"
                            checked={state.contact.signedAgreement === "Y"}
                            size="large"
                            onClick={() =>
                              handleInputChange(state.contact.signedAgreement === "Y" ? null : "Y", "signedAgreement")
                            }
                          />
                        </Form.Field>
                      </Grid.Column>
                      <Grid.Column>
                        <Form.Field>
                          <Checkbox
                            label="N"
                            checked={state.contact.signedAgreement === "N"}
                            size="large"
                            onClick={() =>
                              handleInputChange(state.contact.signedAgreement === "N" ? null : "N", "signedAgreement")
                            }
                          />
                        </Form.Field>
                      </Grid.Column>
                      <Grid.Column>
                        <Form.Field>
                          <Checkbox
                            label="D (declined)"
                            checked={state.contact.signedAgreement === "D"}
                            size="large"
                            onClick={() =>
                              handleInputChange(state.contact.signedAgreement === "D" ? null : "D", "signedAgreement")
                            }
                          />
                        </Form.Field>
                      </Grid.Column>
                      <Grid.Column>
                        <Form.Field>
                          <Checkbox
                            label="N/A"
                            checked={state.contact.signedAgreement === "N/A"}
                            size="large"
                            onClick={() =>
                              handleInputChange(
                                state.contact.signedAgreement === "N/A" ? null : "N/A",
                                "signedAgreement"
                              )
                            }
                          />
                        </Form.Field>
                      </Grid.Column>
                    </Grid.Row>{" "}
                  </React.Fragment>
                )}
              </React.Fragment>
            )}
            <Grid.Row columns={1}>
              <Grid.Column>
                <Form.Field>
                  <label>Tag</label>
                  <Dropdown
                    size="large"
                    options={state.contactTags}
                    placeholder="Choose Tag"
                    search
                    selection
                    allowAdditions
                    style={{ fontSize: 16 }}
                    value={state.contact.contactTagId}
                    onAddItem={(_, { value }) =>
                      setState(state => ({
                        ...state,
                        newTag: value,
                        contactTags: [
                          ...state.contactTags.filter(c => c.value !== 0),
                          {
                            key: 0,
                            text: value,
                            value: 0
                          }
                        ],
                        contact: { ...state.contact, contactTagId: 0 }
                      }))
                    }
                    onChange={(e, { value, text }) => {
                      setState({
                        ...state,
                        contact: {
                          ...state.contact,
                          contactTagId: value,
                          isPrimary: value === 1,
                          isGlobal: value === 2,
                          isLastOption: value === 3
                        },
                        newTag: text
                      });
                    }}
                  />
                </Form.Field>
              </Grid.Column>
            </Grid.Row>
            <Grid.Row columns={1}>
              <Grid.Column>
                <Form.Field>
                  <label>Custom Tag</label>
                  <Input
                    size="large"
                    placeholder="Custom Tag"
                    value={state.contact.customTag}
                    onChange={(_, { value }) => handleInputChange(value, "customTag")}
                    autoComplete="new-password"
                  />
                </Form.Field>
              </Grid.Column>
            </Grid.Row>
            <Grid.Row columns={2}>
              <Grid.Column>
                <Form.Field>
                  <label>Lookup Code</label>
                  <Input
                    size="large"
                    value={state.contact.lookupCode}
                    onChange={(_, { value }) => handleInputChange(value, "lookupCode")}
                    autoComplete="new-password"
                  />
                </Form.Field>
              </Grid.Column>
            </Grid.Row>
            <Grid.Row columns={1}>
              <Grid.Column>
                <Form.Field required>
                  <FileUpload
                    fileList={state.files}
                    setFileToApp={files =>
                      setState(state => ({
                        ...state,
                        files: [...state.files, ...files]
                      }))
                    }
                    deleteFileToApp={fileName =>
                      setState(state => ({
                        ...state,
                        files: [...state.files.filter(f => f.name !== fileName)]
                      }))
                    }
                    multiple={true}
                  />
                </Form.Field>
                {state.contact.contactFiles &&
                  state.contact.contactFiles.map(f => (
                    <p
                      key={shortid.generate()}
                      onClick={() =>
                        setState(state => ({
                          ...state,
                          filesToDelete: [...state.filesToDelete, f.id],
                          contact: {
                            ...state.contact,
                            contactFiles: [...state.contact.contactFiles.filter(cf => cf.id !== f.id)]
                          }
                        }))
                      }
                      className={css({
                        "&:hover": { color: "#707898" },
                        cursor: "pointer"
                      })}
                    >
                      <i className="fal fa-trash-alt" style={{ marginRight: 8 }} />
                      {f.fileName}
                    </p>
                  ))}
              </Grid.Column>
            </Grid.Row>
            <Grid.Row columns={1}>
              <Grid.Column>
                <Form.Field>
                  <TextArea
                    placeholder="Note"
                    style={{ minHeight: 192 }}
                    value={state.contact.note}
                    onChange={(_, { value }) =>
                      setState(state => ({
                        ...state,
                        contact: { ...state.contact, note: value }
                      }))
                    }
                    data-automation="note"
                  />
                </Form.Field>
              </Grid.Column>
            </Grid.Row>
            <Grid.Row columns={1}>
              <Grid.Column>
                <Message
                  visible={state.submitAttempted && state.errorList.length > 0}
                  error
                  header="There was some errors with your submission"
                  list={state.errorList}
                  data-automation="message"
                />
              </Grid.Column>
            </Grid.Row>
            <Grid.Row columns={1}>
              <Grid.Column floated="right" className={css({ textAlign: "right" })}>
                <Button size="large" className="cancelButton" onClick={() => history.goBack()} data-automation="cancel">
                  Cancel
                </Button>
                <Button
                  size="large"
                  className="saveButton"
                  onClick={() => {
                    if (state.contact.isPrimary) {
                      checkIfStateHasPrimaryContact();
                    } else {
                      saveChanges();
                    }
                  }}
                  data-automation="save"
                >
                  {state.submitting ? "Submitting..." : "Save"}
                </Button>
              </Grid.Column>
            </Grid.Row>
          </Grid>
        </Form>
      </div>
      {state.confirmSaveAsPrimary && (
        <Modal
          size={"tiny"}
          open={state.confirmSaveAsPrimary !== null}
          onClose={() => setState(state => ({ ...state, saveAsPrimaryConfirmed: null }))}
          dimmer="inverted"
          data-automation="confirmModal"
        >
          <Modal.Header style={{ fontWeight: "normal" }}>
            Another{" "}
            <strong>
              primary {state.departments.find(d => d.value === state.contact.departmentId).text.toLowerCase()}
            </strong>{" "}
            exists for the following&nbsp;states!
          </Modal.Header>
          <Modal.Content>
            <p>
              {state.states
                .filter(s => state.statesWithPrimaryContact.some(spc => spc === s.value))
                .map(s => (
                  <li key={shortid.generate()}>{s.text}</li>
                ))}
            </p>
          </Modal.Content>
          <Modal.Actions>
            <Button
              negative
              onClick={() =>
                setState(state => ({
                  ...state,
                  confirmSaveAsPrimary: false,
                  saveAsPrimaryConfirmed: false
                }))
              }
              style={{
                backgroundColor: "#fff",
                border: "solid 1px #e5e5ea",
                color: "rgba(0, 0, 0, 0.87)"
              }}
              data-automation="confirmCancel"
            >
              Cancel
            </Button>
            <Button
              positive
              onClick={() => {
                setState(state => ({
                  ...state,
                  saveAsPrimaryConfirmed: true,
                  confirmSaveAsPrimary: false
                }));
                saveChanges();
              }}
              style={{ backgroundColor: "#1f2b5e" }}
              data-automation="confirmContinue"
            >
              Continue
            </Button>
          </Modal.Actions>
        </Modal>
      )}
    </React.Fragment>
  );
}
